import { MetricCard } from "design-system/organisms/insights/MetricCard"
import { formatNumber, formatPercent } from "lopermut"
import { useGetBackofficeWhatsappRoutesQuery } from "modules/backoffice/hooks/whatsappRoutes/useGetBackofficeWhatsappRoutesQuery"
import { useTranslation } from "react-i18next"
import { BackofficeWhatsappRoutesResponseDataSchema } from "schema"
import { z } from "zod"
import { BlockedConversations } from "./BlockedConversations"
import { RoutesTable } from "./RoutesTable"

type Decisions = z.infer<typeof BackofficeWhatsappRoutesResponseDataSchema>["decisions"]

export function BackofficeWhatsappRoutes() {
  const { t } = useTranslation()
  const { data, isPending } = useGetBackofficeWhatsappRoutesQuery()

  return (
    <div className="flex flex-col gap-8">
      <section className="flex flex-col gap-2">
        <div className="flex flex-col gap-0.5">
          <h2 className="text-lg font-medium">{t("backoffice.whatsappRoutes.decisions.title")}</h2>
          <span className="text-sm text-muted-foreground">
            {t("backoffice.whatsappRoutes.decisions.description", {
              count: data?.decisions.total ?? 0,
            })}
          </span>
        </div>
        <DecisionMetrics decisions={data?.decisions} isPending={isPending} />
      </section>
      <section className="flex flex-col gap-2">
        <div className="flex flex-col gap-0.5">
          <h2 className="text-lg font-medium">{t("backoffice.whatsappRoutes.routes.title")}</h2>
          <span className="text-sm text-muted-foreground">
            {t("backoffice.whatsappRoutes.routes.description")}
          </span>
        </div>
        <RoutesTable routes={data?.routes} isPending={isPending} />
      </section>
      <BlockedConversations />
    </div>
  )
}

type DecisionMetricsProps = {
  decisions: Decisions | undefined
  isPending: boolean
}

function DecisionMetrics({ decisions, isPending }: DecisionMetricsProps) {
  const { t } = useTranslation()

  const sendsWithOutcome = (decisions?.sends.delivered ?? 0) + (decisions?.sends.blocked ?? 0)
  const probesWithOutcome = (decisions?.probes.delivered ?? 0) + (decisions?.probes.blocked ?? 0)

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
      <MetricCard
        title={t("backoffice.whatsappRoutes.decisions.metrics.misses.title")}
        tooltip={t("backoffice.whatsappRoutes.decisions.metrics.misses.tooltip")}
        currentValue={formatRate(decisions?.sends.blocked, sendsWithOutcome)}
        metric={t("backoffice.whatsappRoutes.decisions.metrics.misses.detail", {
          blocked: decisions?.sends.blocked ?? 0,
          total: sendsWithOutcome,
          pending: decisions?.sends.pending ?? 0,
        })}
        isPending={isPending}
      />
      <MetricCard
        title={t("backoffice.whatsappRoutes.decisions.metrics.skipped.title")}
        tooltip={t("backoffice.whatsappRoutes.decisions.metrics.skipped.tooltip")}
        currentValue={formatNumber({ amount: decisions?.skipped ?? 0, fix: 0 })}
        metric={t("backoffice.whatsappRoutes.decisions.metrics.skipped.detail", {
          rate: formatRate(decisions?.skipped, decisions?.total),
        })}
        isPending={isPending}
      />
      <MetricCard
        title={t("backoffice.whatsappRoutes.decisions.metrics.probes.title")}
        tooltip={t("backoffice.whatsappRoutes.decisions.metrics.probes.tooltip")}
        currentValue={formatRate(decisions?.probes.delivered, probesWithOutcome)}
        metric={t("backoffice.whatsappRoutes.decisions.metrics.probes.detail", {
          delivered: decisions?.probes.delivered ?? 0,
          total: probesWithOutcome,
          pending: decisions?.probes.pending ?? 0,
        })}
        isPending={isPending}
      />
      <MetricCard
        title={t("backoffice.whatsappRoutes.decisions.metrics.rerouted.title")}
        tooltip={t("backoffice.whatsappRoutes.decisions.metrics.rerouted.tooltip")}
        currentValue={formatNumber({
          amount:
            (decisions?.rerouted.delivered ?? 0) +
            (decisions?.rerouted.blocked ?? 0) +
            (decisions?.rerouted.pending ?? 0),
          fix: 0,
        })}
        metric={t("backoffice.whatsappRoutes.decisions.metrics.rerouted.detail", {
          delivered: decisions?.rerouted.delivered ?? 0,
          blocked: decisions?.rerouted.blocked ?? 0,
        })}
        isPending={isPending}
      />
    </div>
  )
}

function formatRate(part: number | undefined, total: number | undefined) {
  if (!total) return "-"

  return formatPercent({ amount: ((part ?? 0) / total) * 100, fix: 0 })
}
